import type {MithraMaterializedApi} from "~/services/MithraMaterializedApi";
import type {BuGroupField} from "~/services/ApiTypes";
import type {Option} from "~/components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {getSupplierIdHack} from "~/stores/hacks/display";
import type {OptionSource} from "./InsightOptionFilter";

const stringOption = (value: string): Option => ({id: value, label: value || '(Empty)', obj: value});

/**
 * Where each filter of the filter bar gets its options from.
 */
export function makeOptionSources(api: MithraMaterializedApi) {
    const buGroupCache = new Map<BuGroupField, OptionSource>();
    const categoryLevelCache = new Map<number, OptionSource>();
    return {
        categoryL1: ((databagId, params, search) => api.searchInsightL1(databagId, params, search)
            .then(resp => resp.data.map(([l1, total_spend]) => ({
                id: l1, label: l1, subTotalSpend: Number(total_spend), obj: {id: l1, total_spend},
            })))) as OptionSource,
        supplier: ((databagId, params, search) => api.searchInsightSupplier(databagId, params, search)
            .then(resp => resp.data.map(obj => ({
                id: obj.s_id, label: obj.s_name, subLabel: getSupplierIdHack(obj), subTotalSpend: Number(obj.total_spend), obj,
            })))) as OptionSource,
        parentSupplier: ((databagId, params, search) => api.searchInsightParentSupplier(databagId, params, search)
            .then(resp => resp.data.map(obj => ({
                id: obj.sp_id, label: obj.sp_name, subLabel: obj.sp_id, subTotalSpend: Number(obj.total_spend), obj,
            })))) as OptionSource,
        businessUnit: ((databagId, params, search) => api.searchInsightBusinessUnits(databagId, params, search)
            .then(resp => resp.data.map(obj => ({
                id: obj.id, label: obj.bu_name, subLabel: obj.bu_id, subTotalSpend: Number(obj.total_spend), obj,
            })))) as OptionSource,
        geoRegion: ((databagId, params, search) => api.searchInsightGeoRegions(databagId, params, search)
            .then(resp => resp.data.map(obj => ({
                id: String(obj.id), label: obj.region_long || 'Unknown', subLabel: obj.country_name ?? '',
                subTotalSpend: Number(obj.total_spend), obj,
            })))) as OptionSource,
        cDescription: ((databagId, params, search) => api.searchInsightCDescriptions(databagId, params, search)
            .then(resp => resp.data.map(stringOption))) as OptionSource,
        plant: ((databagId, params, search) => api.searchInsightPlants(databagId, params, search)
            .then(resp => resp.data.map(stringOption))) as OptionSource,
        cAddress: ((databagId, params, search) => api.searchInsightCAddress(databagId, params, search)
            .then(resp => resp.data.map(stringOption))) as OptionSource,
        /** L2 to L4; the same label under several parents is one option */
        categoryLevel(level: number): OptionSource {
            let source = categoryLevelCache.get(level);
            if (!source) {
                source = (databagId, params, search) => api.searchInsightCategories(databagId, level, params, search)
                    .then(resp => resp.data.map(({label, total_spend, parents}) => ({
                        id: label,
                        label,
                        subLabel: (parents ?? []).filter(Boolean).join(', '),
                        subTotalSpend: Number(total_spend),
                        obj: {label, total_spend, parents},
                    })));
                categoryLevelCache.set(level, source);
            }
            return source;
        },
        buGroup(field: BuGroupField): OptionSource {
            // Keep the same function per field, so the dropdown does not reload its options on every render
            let source = buGroupCache.get(field);
            if (!source) {
                source = (databagId, params, search) => api.searchInsightBuGroups(databagId, field, params, search)
                    .then(resp => resp.data.map(({value, total_spend}) => ({
                        id: value, label: value || '(Empty)', subTotalSpend: Number(total_spend), obj: {value, total_spend},
                    })));
                buGroupCache.set(field, source);
            }
            return source;
        },
    };
}

export type OptionSources = ReturnType<typeof makeOptionSources>;
